import type { Edge, Node } from 'reactflow'
import type { AdpPipelineSessionV1 } from '@/features/adp/utils/adpPipelineSessionStorage'

export type PipelineDefinitionNodeType = 'source' | 'transformation' | 'integration' | 'destination' | 'unknown'

export type PipelineDefinitionNodeSummary = {
  nodeId: string
  nodeType: PipelineDefinitionNodeType
  connectorId?: string
  label?: string
}

export type PipelineDefinitionV1 = {
  version: 1
  dataFlowId: number | null
  sessionId: string | null
  updatedAt: string
  graph: {
    nodes: Node[]
    edges: Edge[]
  }
  session: AdpPipelineSessionV1
  summary: {
    nodes: PipelineDefinitionNodeSummary[]
    edgeCount: number
  }
}

type PipelineDefinitionBuildArgs = {
  dataFlowId: number | null
  sessionId: string | null
  nodes: Node[]
  edges: Edge[]
  session: AdpPipelineSessionV1
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function normalizeSession(raw: unknown): AdpPipelineSessionV1 {
  const record = asRecord(raw)
  const base: AdpPipelineSessionV1 = {
    version: 1,
    assets: {},
    transformations: {},
    integrations: {},
    sourceConfigDrafts: {},
    columnMapping: [],
    fullOperationJson: {},
    integrationChainOutputs: {},
    selectedRegisteredSources: {},
  }
  if (!record) return base

  return {
    ...base,
    assets: asRecord(record.assets) ? (record.assets as AdpPipelineSessionV1['assets']) : {},
    transformations: asRecord(record.transformations)
      ? (record.transformations as AdpPipelineSessionV1['transformations'])
      : {},
    integrations: asRecord(record.integrations)
      ? (record.integrations as AdpPipelineSessionV1['integrations'])
      : {},
    sourceConfigDrafts: asRecord(record.sourceConfigDrafts)
      ? (record.sourceConfigDrafts as AdpPipelineSessionV1['sourceConfigDrafts'])
      : {},
    columnMapping: Array.isArray(record.columnMapping)
      ? (record.columnMapping as AdpPipelineSessionV1['columnMapping'])
      : [],
    fullOperationJson: asRecord(record.fullOperationJson)
      ? (record.fullOperationJson as Record<string, unknown>)
      : {},
    integrationChainOutputs: asRecord(record.integrationChainOutputs)
      ? (record.integrationChainOutputs as AdpPipelineSessionV1['integrationChainOutputs'])
      : {},
    selectedRegisteredSources: asRecord(record.selectedRegisteredSources)
      ? (record.selectedRegisteredSources as AdpPipelineSessionV1['selectedRegisteredSources'])
      : {},
    sessionId: typeof record.sessionId === 'string' ? record.sessionId : undefined,
  }
}

function getNodeType(node: Node): PipelineDefinitionNodeType {
  const data = asRecord(node.data)
  const groupId = typeof data?.groupId === 'string' ? data.groupId : ''
  if (groupId === 'sources') return 'source'
  if (groupId === 'transformations') return 'transformation'
  if (groupId === 'integrations') return 'integration'
  if (groupId === 'destinations') return 'destination'
  return 'unknown'
}

export function buildPipelineDefinition(args: PipelineDefinitionBuildArgs): PipelineDefinitionV1 {
  const nodes = cloneJson(args.nodes)
  const edges = cloneJson(args.edges)
  const session = normalizeSession(cloneJson(args.session))

  return {
    version: 1,
    dataFlowId: args.dataFlowId,
    sessionId: args.sessionId,
    updatedAt: new Date().toISOString(),
    graph: {
      nodes,
      edges,
    },
    session,
    summary: {
      nodes: nodes.map(node => {
        const data = asRecord(node.data)
        return {
          nodeId: node.id,
          nodeType: getNodeType(node),
          connectorId: typeof data?.connectorId === 'string' ? data.connectorId : undefined,
          label: typeof data?.label === 'string' ? data.label : undefined,
        }
      }),
      edgeCount: edges.length,
    },
  }
}

export function parsePipelineDefinition(raw: unknown): PipelineDefinitionV1 | null {
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    const record = asRecord(parsed)
    if (!record) return null
    if (record.version !== 1) return null
    const graph = asRecord(record.graph)
    if (!graph) return null
    const nodes = Array.isArray(graph.nodes) ? (graph.nodes as Node[]) : []
    const edges = Array.isArray(graph.edges) ? (graph.edges as Edge[]) : []
    const session = normalizeSession(record.session)
    const summaryRecord = asRecord(record.summary)
    const summaryNodes = Array.isArray(summaryRecord?.nodes)
      ? (summaryRecord?.nodes as PipelineDefinitionNodeSummary[])
      : []

    return {
      version: 1,
      dataFlowId: typeof record.dataFlowId === 'number' ? record.dataFlowId : null,
      sessionId: typeof record.sessionId === 'string' ? record.sessionId : null,
      updatedAt: typeof record.updatedAt === 'string' ? record.updatedAt : new Date(0).toISOString(),
      graph: { nodes, edges },
      session,
      summary: {
        nodes: summaryNodes,
        edgeCount: typeof summaryRecord?.edgeCount === 'number' ? summaryRecord.edgeCount : edges.length,
      },
    }
  } catch {
    return null
  }
}

export function createEmptyPipelineDefinitionSession(): AdpPipelineSessionV1 {
  return {
    version: 1,
    assets: {},
    transformations: {},
    integrations: {},
    sourceConfigDrafts: {},
    columnMapping: [],
    fullOperationJson: {},
    integrationChainOutputs: {},
    selectedRegisteredSources: {},
  }
}
